import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, AlertCircle } from "lucide-react";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      const hashParams   = new URLSearchParams(window.location.hash.substring(1));
      const accessToken  = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type         = hashParams.get('type');
  
      if (type !== 'recovery' || !accessToken || !refreshToken) {
        setTokenValid(false);
        return;
      }
  
      try {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token:  accessToken,
          refresh_token: refreshToken,
        });
        if (sessionError) throw sessionError;
  
        const {
          data: { user },
        } = await supabase.auth.getUser();
        setTokenValid(!!user);
      } catch (e) {
        console.error('Error verifying token:', e);
        setTokenValid(false);
      }
    };
  
    verifyToken();
  }, []);
  

  const validatePassword = (password: string) => {
    if (password.length < 6) {
      setValidationError("Password must be at least 6 characters long");
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isSupabaseConfigured()) {
      toast({
        title: "Configuration Error",
        description: "Supabase is not properly configured.",
        variant: "destructive",
      });
      return;
    }

    if (!validatePassword(password)) return;
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);

    try {
      // Update password using Supabase
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      toast({
        title: "Password updated successfully",
        description: "You can now login with your new password",
      });

      navigate("/login");
    } catch (error) {
      console.error("Password update error:", error);
      toast({
        title: "Password Reset Failed",
        description: error.message || "Password reset failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while verifying token
  if (tokenValid === null) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 border border-gray-200 text-center">
          <div className="animate-pulse">
            <h1 className="text-2xl font-bold mb-6">Verifying Reset Link...</h1>
            <p className="text-gray-600">
              Please wait while we verify your password reset link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 border border-gray-200">
          <h1 className="text-2xl font-bold text-center mb-6">
            Invalid Reset Link
          </h1>

          <Alert className="mb-4 bg-yellow-50 border-yellow-100">
            <AlertDescription className="text-yellow-800 flex gap-2 items-start">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <span>
                This reset link is invalid or has expired. Please request a new
                password reset link.
              </span>
            </AlertDescription>
          </Alert>

          <Button
            onClick={() => navigate("/forgot-password")}
            className="w-full"
          >
            Request New Reset Link
          </Button>
        </div>
      </div>
    );
  }

  // Valid token state
  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <div className="bg-white shadow-md rounded-lg p-8 border border-gray-200">
        <h1 className="text-2xl font-bold text-center mb-6">
          Create New Password
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                validatePassword(e.target.value);
              }}
              placeholder="Enter new password"
              required
              className="w-full"
            />
          </div>

          <div className="mb-6">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Confirm New Password
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              className="w-full"
            />

            {validationError && (
              <p className="mt-2 text-sm text-red-600">{validationError}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !!validationError}
          >
            {isSubmitting ? "Updating..." : "Reset Password"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;